import React from 'react';
const { useState, useEffect, useRef } = React;

// The in-app notification center (a bell): the coach's in Acompañamiento and
// each participant's in their portal. The server decides what each one gets;
// texts never carry the content of a message or a reflection. Only inside Oike:
// there are no email, browser or push notifications.
// call(body) runs the API of that audience (coaching-admin or coaching-portal).

const KIND_LABELS = {
  message: 'Mensajes nuevos', booking: 'Reservas', reschedule: 'Cambios de horario', cancellation: 'Cancelaciones',
  assignment: 'Actividades asignadas', shared_entry: 'Reflexiones y avances compartidos',
};
const panel = {
  position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 'min(360px, calc(100vw - 32px))', maxHeight: 420, overflowY: 'auto', zIndex: 80,
  // --card, --text and --border exist in both themes: the app (light, :root) and the person's space (dark, .portal),
  // so the panel always takes the colors of the screen it opens in (never a light panel with light text)
  background: 'var(--card, #fff)', color: 'var(--text, #1E2A2C)', border: '1px solid var(--border, rgba(127,127,127,0.3))', borderRadius: 10,
  boxShadow: '0 10px 30px rgba(0,0,0,0.35)', padding: 8,
};
const when = (iso) => { try { return new Date(iso).toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' }); } catch { return ''; } };

export default function NotificationCenter({ call, onOpen, prefsBody = {}, buttonClass = 'action-btn' }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [prefs, setPrefs] = useState(null);
  const ref = useRef(null);
  const load = () => call({ action: 'notifications' }).then(d => { setData(d); setError(''); }).catch(e => setError(e.message || 'No se pudieron cargar los avisos.'));
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const mark = async (body) => { try { await call({ action: 'markNotificationsRead', ...body }); load(); } catch (e) { setError(e.message); } };
  const go = async (n) => { if (!n.read) await mark({ ids: [n.id] }); setOpen(false); onOpen(n); };
  const loadPrefs = async () => { try { setPrefs((await call({ action: 'notificationPrefs', ...prefsBody })).prefs); } catch (e) { setError(e.message); } };
  const savePrefs = async (next) => { setPrefs(next); try { await call({ action: 'saveNotificationPrefs', ...prefsBody, prefs: next }); } catch (e) { setError(e.message); } };
  const unread = data?.unread || 0;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className={buttonClass} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(v => !v)} aria-label={unread ? `Avisos: ${unread} sin leer` : 'Avisos'}>
        🔔{unread > 0 && <span style={{ marginLeft: 4, background: 'var(--globant-green, #2a9d8f)', color: 'var(--globant-dark, #fff)', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 700 }}>{unread}</span>}
      </button>
      {open && (
        <div role="dialog" aria-label="Avisos" style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, padding: '2px 4px 8px' }}>
            <b style={{ fontSize: 13 }}>Avisos</b>
            <span style={{ display: 'flex', gap: 4 }}>
              {unread > 0 && <button type="button" className={buttonClass} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => mark({ all: true })}>Marcar todo como leído</button>}
              <button type="button" className={buttonClass} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => (prefs ? setPrefs(null) : loadPrefs())}>{prefs ? 'Volver' : 'Preferencias'}</button>
            </span>
          </div>
          {error && <div role="alert" style={{ fontSize: 12, color: '#B4362C', padding: 4 }}>{error}</div>}
          {prefs ? (
            <div style={{ display: 'grid', gap: 6, padding: 4, fontSize: 13 }}>
              <span style={{ fontSize: 12, opacity: 0.75 }}>Qué avisos quieres ver aquí. Solo dentro de Oike: no se envían correos ni notificaciones al teléfono.</span>
              {Object.keys(prefs).map(k => (
                <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={prefs[k] !== false} onChange={e => savePrefs({ ...prefs, [k]: e.target.checked })} /> {KIND_LABELS[k] || k}</label>
              ))}
            </div>
          ) : !data ? <div style={{ fontSize: 12, opacity: 0.7, padding: 8 }}>Cargando…</div>
            : !data.items.length ? <div style={{ fontSize: 12, opacity: 0.7, padding: 8 }}>No hay avisos. Aquí vas a ver mensajes nuevos, reservas y cambios.</div>
            : <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
              {data.items.map(n => (
                <li key={n.id}>
                  <button type="button" onClick={() => go(n)} style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', color: 'inherit', font: 'inherit', background: n.read ? 'transparent' : 'rgba(91,191,181,0.10)' }}>
                    <span style={{ fontSize: 13, fontWeight: n.read ? 400 : 700 }}>{n.text}{n.count > 1 ? ` (${n.count})` : ''}</span>
                    <span style={{ display: 'block', fontSize: 11, opacity: 0.7 }}>{n.processName ? `${n.processName} · ` : ''}{when(n.updatedAt || n.createdAt)}{!n.read ? ' · sin leer' : ''}</span>
                  </button>
                </li>
              ))}
            </ul>}
        </div>
      )}
    </div>
  );
}
